import React, { useState, useEffect } from 'react';
import {
  VStack, SimpleGrid, FormControl, FormLabel, Input, Select, Textarea,
  Button, HStack, Checkbox, Box, Link, Text, useToast,
} from '@chakra-ui/react';
import {
  ImmSolicitud, crearSolicitud, actualizarSolicitud,
  listarLocalidades, Localidad, fileUrl,
} from '../../../api/immApi';
import {
  SectionTitle, Field, IMM_COLOR, IMM_COLOR_HOVER, fmt,
} from '../_shared';
import MapSelector from '../../../components/MapSelector';

type Modo = 'new' | 'edit' | 'view';

interface Props {
  modo: Modo;
  data?: ImmSolicitud;
  onGuardado?: (s: ImmSolicitud) => void;
  onCancelar?: () => void;
}

const ESTATUS_OPCIONES = ['Recibida', 'Vinculada', 'Atendida', 'Cerrada'];
const hoy = () => new Date().toISOString().split('T')[0];

const FormularioSolicitud: React.FC<Props> = ({ modo, data, onGuardado, onCancelar }) => {
  const toast = useToast();
  const [localidades, setLocalidades] = useState<Localidad[]>([]);
  const [guardando, setGuardando] = useState(false);
  const [archivos, setArchivos] = useState<File[]>([]);

  const [f, setF] = useState<Partial<ImmSolicitud>>({
    nombre: data?.nombre || '',
    telefono: data?.telefono || '',
    email: data?.email || '',
    situacion: data?.situacion || '',
    tipoApoyo: data?.tipoApoyo || '',
    estado: data?.estado || 'Tlaxcala',
    municipio: data?.municipio || 'Totolac',
    localidad: data?.localidad || '',
    localidadId: data?.localidadId ?? undefined,
    colonia: data?.colonia || '',
    codigoPostal: data?.codigoPostal || '',
    tipoVialidad: data?.tipoVialidad || '',
    vialidad: data?.vialidad || '',
    numeroExterior: data?.numeroExterior || '',
    numeroInterior: data?.numeroInterior || '',
    referencias: data?.referencias || '',
    latitud: data?.latitud ?? undefined,
    longitud: data?.longitud ?? undefined,
    urlMaps: data?.urlMaps || '',
    fecha: data?.fecha ? data.fecha.split('T')[0] : hoy(),
    hora: data?.hora || '',
    atencionInmediata: data?.atencionInmediata ?? false,
    disponibilidad: data?.disponibilidad || '',
    estatus: data?.estatus || 'Recibida',
  });

  useEffect(() => {
    listarLocalidades().then(setLocalidades).catch(() => setLocalidades([]));
  }, []);

  const set = (campo: keyof ImmSolicitud, valor: any) => setF((prev) => ({ ...prev, [campo]: valor }));

  const handleUbicacion = (lat: number, lng: number, address: any) => {
    setF((prev) => ({
      ...prev,
      latitud: lat,
      longitud: lng,
      urlMaps: `https://www.google.com/maps?q=${lat},${lng}`,
      colonia: prev.colonia || address?.neighborhood || '',
      codigoPostal: prev.codigoPostal || address?.postalCode || '',
    }));
  };

  const handleGuardar = async () => {
    if (!f.nombre?.trim()) {
      toast({ title: 'El nombre es obligatorio', status: 'warning', duration: 3000 });
      return;
    }
    if (!f.tipoApoyo?.trim()) {
      toast({ title: 'El tipo de apoyo es obligatorio', status: 'warning', duration: 3000 });
      return;
    }
    setGuardando(true);
    try {
      let res: ImmSolicitud;
      if (modo === 'edit' && data) {
        const payload: Partial<ImmSolicitud> = {
          nombre: f.nombre,
          telefono: f.telefono,
          email: f.email,
          situacion: f.situacion,
          tipoApoyo: f.tipoApoyo,
          estado: f.estado,
          municipio: f.municipio,
          localidad: f.localidad,
          localidadId: f.localidadId,
          colonia: f.colonia,
          codigoPostal: f.codigoPostal,
          tipoVialidad: f.tipoVialidad,
          vialidad: f.vialidad,
          numeroExterior: f.numeroExterior,
          numeroInterior: f.numeroInterior,
          referencias: f.referencias,
          latitud: f.latitud != null && String(f.latitud) !== '' ? Number(f.latitud) : undefined,
          longitud: f.longitud != null && String(f.longitud) !== '' ? Number(f.longitud) : undefined,
          urlMaps: f.urlMaps,
          fecha: f.fecha || undefined,
          hora: f.hora,
          atencionInmediata: !!f.atencionInmediata,
          disponibilidad: f.disponibilidad,
          estatus: f.estatus,
        };
        res = await actualizarSolicitud(data.id, payload);
      } else {
        const fd = new FormData();
        fd.append('nombre', f.nombre || '');
        fd.append('telefono', f.telefono || '');
        fd.append('email', f.email || '');
        fd.append('situacion', f.situacion || '');
        fd.append('tipoApoyo', f.tipoApoyo || '');
        fd.append('estado', f.estado || '');
        fd.append('municipio', f.municipio || '');
        fd.append('localidad', f.localidad || '');
        if (f.localidadId != null) fd.append('localidadId', String(f.localidadId));
        fd.append('colonia', f.colonia || '');
        fd.append('codigoPostal', f.codigoPostal || '');
        fd.append('tipoVialidad', f.tipoVialidad || '');
        fd.append('vialidad', f.vialidad || '');
        fd.append('numeroExterior', f.numeroExterior || '');
        fd.append('numeroInterior', f.numeroInterior || '');
        fd.append('referencias', f.referencias || '');
        if (f.latitud != null && String(f.latitud) !== '') fd.append('latitud', String(f.latitud));
        if (f.longitud != null && String(f.longitud) !== '') fd.append('longitud', String(f.longitud));
        if (f.urlMaps) fd.append('urlMaps', f.urlMaps);
        if (f.fecha) fd.append('fecha', f.fecha as string);
        fd.append('hora', f.hora || '');
        fd.append('atencionInmediata', String(!!f.atencionInmediata));
        fd.append('disponibilidad', f.disponibilidad || '');
        archivos.forEach((file) => fd.append('archivos', file));
        res = await crearSolicitud(fd);
      }
      toast({ title: modo === 'edit' ? 'Solicitud actualizada' : 'Solicitud creada', status: 'success', duration: 3000 });
      onGuardado?.(res);
    } catch (error: any) {
      toast({ title: 'Error', description: error?.response?.data?.message || 'No se pudo guardar la solicitud.', status: 'error', duration: 4000 });
    } finally {
      setGuardando(false);
    }
  };

  if (modo === 'view' && data) {
    const direccion = [
      data.tipoVialidad, data.vialidad,
      data.numeroExterior ? `No. ${data.numeroExterior}` : '',
      data.numeroInterior ? `Int. ${data.numeroInterior}` : '',
    ].filter(Boolean).join(' ');
    return (
      <VStack spacing={3} align="stretch">
        <SectionTitle>Datos de contacto</SectionTitle>
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
          <Field label="Folio" value={data.folio} />
          <Field label="Nombre" value={data.nombre} />
          <Field label="Teléfono" value={data.telefono} />
          <Field label="Correo electrónico" value={data.email} />
          <Field label="Tipo de apoyo" value={data.tipoApoyo} />
          <Field label="Estatus" value={data.estatus} />
        </SimpleGrid>

        <SectionTitle>Situación</SectionTitle>
        <SimpleGrid columns={{ base: 1 }} spacing={4}>
          <Field label="Situación" value={data.situacion} />
        </SimpleGrid>

        <SectionTitle>Domicilio</SectionTitle>
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
          <Field label="Localidad" value={data.localidad} />
          <Field label="Colonia" value={data.colonia} />
          <Field label="Código postal" value={data.codigoPostal} />
          <Field label="Dirección" value={direccion} />
          <Field label="Referencias" value={data.referencias} />
          {(data.urlMaps || (data.latitud != null && data.longitud != null)) && (
            <Box>
              <Text fontSize="xs" color="gray.500" fontWeight="bold">Ubicación</Text>
              <Link
                href={data.urlMaps || `https://www.google.com/maps?q=${data.latitud},${data.longitud}`}
                isExternal color={IMM_COLOR} fontSize="sm" fontWeight="medium"
              >
                Ver en Google Maps
              </Link>
            </Box>
          )}
        </SimpleGrid>

        <SectionTitle>Disponibilidad y atención</SectionTitle>
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
          <Field label="Fecha" value={fmt(data.fecha)} />
          <Field label="Hora" value={data.hora} />
          <Field label="Disponibilidad" value={data.disponibilidad} />
          <Field label="Atención inmediata" value={data.atencionInmediata ? 'Sí' : 'No'} />
        </SimpleGrid>

        <SectionTitle>Archivos adjuntos</SectionTitle>
        <Box>
          {data.archivos && data.archivos.length > 0 ? (
            <VStack align="stretch" spacing={1}>
              {data.archivos.map((a) => (
                <Link key={a.id} href={fileUrl(a.rutaArchivo)} isExternal color={IMM_COLOR} fontSize="sm" fontWeight="medium">
                  {a.nombreOriginal || a.rutaArchivo}
                </Link>
              ))}
            </VStack>
          ) : (
            <Text fontSize="sm" color="gray.400">Sin archivos adjuntos</Text>
          )}
        </Box>

        <HStack justify="flex-end" pt={2}>
          <Button variant="outline" onClick={onCancelar}>Cerrar</Button>
        </HStack>
      </VStack>
    );
  }

  return (
    <VStack spacing={3} align="stretch">
      <SectionTitle>Datos de contacto</SectionTitle>
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
        <FormControl isRequired>
          <FormLabel fontSize="sm">Nombre</FormLabel>
          <Input value={f.nombre || ''} onChange={(e) => set('nombre', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Teléfono</FormLabel>
          <Input value={f.telefono || ''} onChange={(e) => set('telefono', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Correo electrónico</FormLabel>
          <Input type="email" value={f.email || ''} onChange={(e) => set('email', e.target.value)} />
        </FormControl>
        <FormControl isRequired>
          <FormLabel fontSize="sm">Tipo de apoyo</FormLabel>
          <Input value={f.tipoApoyo || ''} onChange={(e) => set('tipoApoyo', e.target.value)} />
        </FormControl>
        {modo === 'edit' && (
          <FormControl>
            <FormLabel fontSize="sm">Estatus</FormLabel>
            <Select value={f.estatus || 'Recibida'} onChange={(e) => set('estatus', e.target.value)}>
              {ESTATUS_OPCIONES.map((v) => <option key={v} value={v}>{v}</option>)}
            </Select>
          </FormControl>
        )}
      </SimpleGrid>

      <SectionTitle>Situación</SectionTitle>
      <SimpleGrid columns={{ base: 1 }} spacing={4}>
        <FormControl>
          <FormLabel fontSize="sm">Situación</FormLabel>
          <Textarea rows={3} value={f.situacion || ''} onChange={(e) => set('situacion', e.target.value)} />
        </FormControl>
      </SimpleGrid>

      <SectionTitle>Domicilio</SectionTitle>
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
        <FormControl>
          <FormLabel fontSize="sm">Estado</FormLabel>
          <Input value={f.estado || ''} onChange={(e) => set('estado', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Municipio</FormLabel>
          <Input value={f.municipio || ''} onChange={(e) => set('municipio', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Localidad</FormLabel>
          <Select placeholder="Selecciona localidad" value={f.localidad || ''} onChange={(e) => {
            const loc = localidades.find((l) => l.descripcion === e.target.value);
            set('localidad', e.target.value);
            set('localidadId', loc?.id);
          }}>
            {localidades.map((l) => <option key={l.id} value={l.descripcion}>{l.descripcion}</option>)}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Colonia</FormLabel>
          <Input value={f.colonia || ''} onChange={(e) => set('colonia', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Código postal</FormLabel>
          <Input value={f.codigoPostal || ''} onChange={(e) => set('codigoPostal', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Tipo de vialidad</FormLabel>
          <Input placeholder="Calle, Avenida, Privada..." value={f.tipoVialidad || ''} onChange={(e) => set('tipoVialidad', e.target.value)} />
        </FormControl>
        <FormControl gridColumn={{ md: 'span 2' }}>
          <FormLabel fontSize="sm">Vialidad (nombre)</FormLabel>
          <Input value={f.vialidad || ''} onChange={(e) => set('vialidad', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">No. exterior</FormLabel>
          <Input value={f.numeroExterior || ''} onChange={(e) => set('numeroExterior', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">No. interior</FormLabel>
          <Input value={f.numeroInterior || ''} onChange={(e) => set('numeroInterior', e.target.value)} />
        </FormControl>
        <FormControl gridColumn={{ md: 'span 2' }}>
          <FormLabel fontSize="sm">Referencias</FormLabel>
          <Input value={f.referencias || ''} onChange={(e) => set('referencias', e.target.value)} />
        </FormControl>
        <Box gridColumn={{ md: 'span 3' }}>
          <FormLabel fontSize="sm">Ubicación en el mapa</FormLabel>
          <MapSelector
            initialLat={f.latitud ?? undefined}
            initialLng={f.longitud ?? undefined}
            onLocationSelect={handleUbicacion}
          />
          {f.latitud != null && f.longitud != null && (
            <Link
              href={f.urlMaps || `https://www.google.com/maps?q=${f.latitud},${f.longitud}`}
              isExternal color={IMM_COLOR} fontSize="sm" fontWeight="medium" mt={2} display="inline-block"
            >
              Ver en Google Maps
            </Link>
          )}
        </Box>
      </SimpleGrid>

      <SectionTitle>Disponibilidad y atención</SectionTitle>
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
        <FormControl>
          <FormLabel fontSize="sm">Fecha</FormLabel>
          <Input type="date" value={(f.fecha as string) || ''} onChange={(e) => set('fecha', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Hora</FormLabel>
          <Input type="time" value={f.hora || ''} onChange={(e) => set('hora', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Disponibilidad</FormLabel>
          <Input value={f.disponibilidad || ''} onChange={(e) => set('disponibilidad', e.target.value)} />
        </FormControl>
        <FormControl>
          <Checkbox isChecked={!!f.atencionInmediata} onChange={(e) => set('atencionInmediata', e.target.checked)}>
            Requiere atención inmediata
          </Checkbox>
        </FormControl>
      </SimpleGrid>

      {modo === 'new' && (
        <>
          <SectionTitle>Archivos adjuntos</SectionTitle>
          <FormControl>
            <FormLabel fontSize="sm">Documentos (imágenes o PDF)</FormLabel>
            <Input
              type="file"
              multiple
              accept="image/*,application/pdf"
              p={1}
              onChange={(e) => setArchivos(e.target.files ? Array.from(e.target.files) : [])}
            />
            {archivos.length > 0 && (
              <Text fontSize="xs" color="gray.500" mt={1}>{archivos.length} archivo(s) seleccionado(s)</Text>
            )}
          </FormControl>
        </>
      )}

      <HStack justify="flex-end" pt={2} spacing={3}>
        <Button variant="outline" onClick={onCancelar} isDisabled={guardando}>Cancelar</Button>
        <Button bg={IMM_COLOR} color="white" _hover={{ bg: IMM_COLOR_HOVER }} onClick={handleGuardar} isLoading={guardando} loadingText="Guardando...">
          Guardar solicitud
        </Button>
      </HStack>
    </VStack>
  );
};

export default FormularioSolicitud;
